import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { Dialog, DialogBody, DialogHeader, Heading, ListSkeleton } from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiQuery } from '../../state';
import { APIErrorBoundary } from '..';
import { InviteMembers } from './InviteMembers';
import { PermissionDeniedDialogContent } from './ShareContentPermissionDenied';
import type { ShareContentModalProps } from './types';

/**
 * Dialog to invite members to a space or collection.
 */
export function InviteMembersDialog(
    props: Omit<ShareContentModalProps, 'organization'>
): React.ReactElement | null {
    const { content } = props;
    const name = 'space' in content ? content.space.title : content.collection.title;

    if (
        ('space' in content && !content.space.permissions.admin) ||
        ('collection' in content && !content.collection.permissions.admin)
    ) {
        return (
            <Dialog>
                <DialogBody>
                    <PermissionDeniedDialogContent />
                </DialogBody>
            </Dialog>
        );
    }

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Share {name}</Heading>
            </DialogHeader>
            <DialogBody>
                <ErrorBoundary>
                    <APIErrorBoundary
                        fallback={{
                            401: <PermissionDeniedDialogContent />,
                            403: <PermissionDeniedDialogContent />,
                        }}
                    >
                        <React.Suspense fallback={<ListSkeleton items={1} />}>
                            <Content {...props} />
                        </React.Suspense>
                    </APIErrorBoundary>
                </ErrorBoundary>
            </DialogBody>
        </Dialog>
    );
}

/**
 * Content of the tab of the share modal
 */
function Content(props: Omit<ShareContentModalProps, 'organization'>): React.ReactElement {
    const { content } = props;
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', {
            organizationId:
                'space' in content ? content.space.organization : content.collection.organization,
        })
    );

    useSetAnalyticsScreen(
        'space' in content
            ? analytics.createSpaceEvent(content.space, {
                  screen: 'space_share',
                  screenTab: 'invite',
              })
            : analytics.createCollectionEvent(content.collection, {
                  screen: 'collection_share',
                  screenTab: 'invite',
              })
    );

    return <InviteMembers content={content} organization={organization} />;
}
